import { ReactElement, useContext, useEffect, useState } from "react";
import Chart from "../../../components/charts/chart";
import { getTicks, useChartDimensions, useUniqueId } from "../../../components/charts/chartUtils";
import * as d3 from "d3";
import AxisHorizontal from "../../../components/charts/AxisHorizontal";
import {
  IAddition,
  IDelayBase, IDictionary, IEAFProduction, IProduction,
  IProductionExtended,
  IShiftHistory, IStrandProduction,
  IVariableGroup
} from "../../../types";
import { Paper, useTheme } from "@mui/material";
import KeyValueItem from "../../../components/charts/keyValueItem";
import Detail from "../../../components/dialogs/detail";
import HeatInformation from "./heatInformation";
import {
  formatDate,
  formatDateTimeWithSeconds,
  formatTimeWithSeconds,
  getCurrentDatetimeInSeconds
} from "../../../system/utils";
import { Alert } from "@mui/material";
import {
  xAdditionAccessorScaled,
  xProductionBeginAccessorScaled,
  xProductionEndAccessorScaled,
  xSampleAccessorScaled
} from "../utils";
import { SystemContext } from "../../../context/systemContext";
import { getLanguage } from "../configuration/language";
import { getPalette } from "../../../configuration/configuration";
import SkeletonGantt from "./skeletonGantt";
import { DateTime } from "luxon";
import ProductionInformation from "../../mill/components/productionInformation";
import GanttSliderPages from "./ganttSliderPages";
import HtmlTooltip from "../../../components/charts/tooltip";
import DelayLayerGantt from "../../interruptions/compoenents/delayLayerGantt";
import AdditionsLayerGantt from "./additionsLayerGantt";
import VariablesLayerGantt from "../variablesLayerGantt";
import SamplesLayerGantt from "./samplesLayerGantt";
import HeatDetailInfo from "../../meltShop/components/heatDetailInfo";
import StrandDetailInfo from "../../mill/components/strandDetailInfo";

interface IHeatChartProps {
  productionData: IProduction[];
  parentSize: {
    width: number;
    height: number;
  } | null;
}

interface IPage {
  beginDatetime: number;
  endDatetime: number;
  range: number[];
}

const productTooltip = (productionItem: IProductionExtended, detail: ReactElement | null, children: ReactElement): ReactElement => {
  return(
    <HtmlTooltip title={
      <div>
        <div className="display-flex-column align-items-center" >
          <strong>{productionItem.machine.name}</strong>
          <strong>{productionItem.title}</strong>
        </div>
        <KeyValueItem name={"Begin"} value={formatTimeWithSeconds(productionItem.beginDatetime as number)} />
        <KeyValueItem name={"End"} value={formatTimeWithSeconds(productionItem.endDatetime as number)} />
        {detail}
      </div>
    }>
      {children}
    </HtmlTooltip>
  );
};

const heatHeight = 25;
const orderHeight = 30;
// const sliderHeight =  30;
const marginTop = 40;
const marginBottomWithSlider = 95;
const shiftHeader = 30;

const GanttChartParallelMachines = ({ productionData, parentSize }: IHeatChartProps): ReactElement => {
  const chartID = useUniqueId("gantt-chart-parallel-machines");

  // let heightX = 30;
  const height = parentSize ? parentSize.height : 0;
  let totalHeight = 30;

  productionData.forEach((p) => {
    totalHeight += p.productionMachines.length * heatHeight + orderHeight;
  });

  const useScroll = parentSize && totalHeight > height;

  const theme = useTheme();
  const systemContext = useContext(SystemContext);
  const [openDetail, setOpenDetail] = useState(false);
  const [title, setTitle] = useState("");
  const [shifts, setShifts] = useState<IShiftHistory[]>([]);
  const [window, setWindow] = useState({
    maxEndDatetime: 0,
    pageIndex: 0,
    pages: [] as IPage[],
    forward: true
  });

  const [selectedProductionItem, setSelectedProductionItem] = useState<IProductionExtended | null>(null);
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height,
    marginBottom: !useScroll ? 65 : 95,
    marginLeft: 8,
    marginRight: 8,
    marginTop
  });

  useEffect(() => {
    if (productionData && productionData.length > 0 && parentSize) {

      const shiftList: IDictionary<IShiftHistory> = {};

      let min = DateTime.now().toSeconds();
      let max = 0;
      let maxEndTime = 0;

      const pages: IPage[] = [];

      const productionHeightList: number[] = productionData.map(x => x.productionMachines.length * heatHeight + orderHeight);
      const maxHeight = parentSize.height - marginTop -  marginBottomWithSlider - shiftHeader + 5;
      let accumHeight = 0;
      let left = 0;
      let right = 0;

      for (let index = 0; index < productionHeightList.length; index++) {
        const h = productionHeightList[index];
        if (accumHeight + h <= maxHeight) {
          accumHeight += h;
          right = index;
        } else {
          break;
        }
      }

      // First Page
      const endFirstPage = productionData[right].endDatetime ?? getCurrentDatetimeInSeconds();
      pages.push({
        beginDatetime: Math.min(productionData[0].beginDatetime, productionData[0].productionMachines[0].shiftHistory.beginDatetime),
        endDatetime: right < productionData.length - 1 ? endFirstPage : Math.max(endFirstPage, productionData[right].productionMachines[0].shiftHistory.endDatetime),
        range: [left, right + 1]
      });

      const span = pages[0].endDatetime - pages[0].beginDatetime;
      const factor = span / 3;
      let begin =  pages[0].beginDatetime + factor;
      let end =  pages[0].endDatetime + factor;

      productionData.forEach((x) => {
        x.productionMachines.forEach((s) => {
          const key = `${s.shiftHistory.factoryDay}-${s.shiftHistory.name}`;
          if (!shiftList[key]) {
            shiftList[key] = s.shiftHistory;
          }

          min = Math.min(min, s.shiftHistory.factoryDay);
          max = Math.max(max, s.shiftHistory.factoryDay);
          maxEndTime = Math.max(maxEndTime, s.shiftHistory.endDatetime);
        });
      });

      while(begin < maxEndTime) {
        accumHeight = 0;
        left = 0;
        for (let index = 0; index < productionData.length; index++) {
          if ((productionData[index].beginDatetime >= begin && productionData[index].beginDatetime <= end)
          || (productionData[index].beginDatetime < begin && (productionData[index].endDatetime ?? 0) >= end)) {
            const h = productionHeightList[index];

            if (accumHeight + h < maxHeight) {
              accumHeight += h;
              right = index;
              if (left === 0) {
                left = index;
              }
            } else {
              break;
            }
          }
        }

        if (left !== 0) {
          pages.push({
            beginDatetime: begin,
            endDatetime: end, // Math.max(end, productionData[right].endDatetime ?? 0, productionData[right].productionMachines[0].shiftHistory.endDatetime ?? 0),
            range: [left, right + 1]
          });
        }

        begin += factor;
        end += factor;
      }

      const maxEndDatetime = Math.max(maxEndTime, productionData[productionData.length - 1].endDatetime ?? getCurrentDatetimeInSeconds());

      const newWindow = {
        maxEndDatetime,
        pageIndex: 0,
        pages,
        forward: true };

      setWindow(newWindow);
      setTitle(min !== max ? `${formatDate(min)} - ${formatDate(max)}` : `${formatDate(min)}`);
      setShifts(Object.values(shiftList).sort((a, b) => a.beginDatetime - b.beginDatetime));
    }
  }, [productionData, parentSize]);

  if (productionData.length === 0) {
    return (
      <Paper>
        <Alert severity="warning">No Data to Display.</Alert>
      </Paper>
    );
  }

  if (window.pages.length === 0) {
    return <SkeletonGantt/>;
  }

  const xScale = d3.scaleLinear()
    .domain([window.pages[window.pageIndex].beginDatetime, window.pages[window.pageIndex].endDatetime])
    .range([0, dimensions.boundedWidth]);

  const xAccessorScaled = (d: IDelayBase) => xScale(d.beginDatetime);
  const widthAccessorScaled = (d: IDelayBase) => xScale(d.endDatetime ?? getCurrentDatetimeInSeconds()) - xScale(d.beginDatetime);

  const onSelected = (productionItem: IProductionExtended) => {
    setSelectedProductionItem(productionItem);
    setOpenDetail(true);
  };

  const onPageChanged = (pageIndex: number) => {
    setWindow({ ...window, pageIndex });
  };

  const productionFiltered = productionData; // .filter(x => (x.endDatetime ?? getCurrentDatetimeInSeconds()) >= window.beginDatetime  + window.dateOffset && x.beginDatetime < window.endDatetime  + window.dateOffset);

  const sliceRange = window.pages[window.pageIndex].range;

  let heatGroup = heatHeight + 15;
  let currentProductHeight = 0;

  const now = getCurrentDatetimeInSeconds();
  const xCursor = xScale(now);

  return (
    <div ref={ref} style={{ height: dimensions.height }} >
      <Chart dimensions={dimensions}>
        <defs>
          <clipPath id="cut-off">
            <rect x="0" y="0" width={dimensions.boundedWidth} height={dimensions.boundedHeight} />
          </clipPath>
        </defs>

        <AxisHorizontal
          chartID={chartID}
          dimensions={dimensions}
          scale={xScale}
          label={title}
          ticks={getTicks(xScale.domain(), parentSize && parentSize.width > 600 ? 5 : 3)}
          formatTick={(value, index) => index === 0 || index === 4 ? formatDateTimeWithSeconds(value as number) : formatTimeWithSeconds(value as number)}/>

        <g>
          {useScroll &&
            <GanttSliderPages width={dimensions.boundedWidth}
              top={dimensions.boundedHeight + 50}
              totalPages={window.pages.length}
              onPageChanged={onPageChanged}/>
          }
        </g>

        <g transform={`translate(${0}, ${-20})`}>
          <g transform={`translate(${0}, ${-10})`}>
            <polygon fill={"green"} points="5 0 0 5 5 10 10 5 5 0"/>
          </g>
          <text y={0}
            x={12}
            fill={theme.palette.text.primary}
            fontSize="1em"
            fillOpacity="0.9"
            textAnchor="start">
            {getLanguage(systemContext.languageCode).samples}
          </text>

          <g transform={`translate(${78}, ${-10})`}>
            <polygon fill={"#d84315"} points="0 0 5 10 10 0 0 0"/>
          </g>
          <text y={0}
            x={90}
            fill={theme.palette.text.primary}
            fontSize="1em"
            fillOpacity="0.9"
            textAnchor="start">
            {getLanguage(systemContext.languageCode).variables}
          </text>
        </g>

        <g>
          {shifts.map((shift: IShiftHistory, shiftIndex: number): ReactElement | null => {

            const shiftStart = xScale(shift.beginDatetime);
            const shiftEnd = xScale(shift.endDatetime);

            if (shiftStart > dimensions.boundedWidth || shiftEnd < 0) {
              return null;
            }

            const shiftWidth = shiftEnd - shiftStart;

            return(
              <g key={`shift-area-${shiftIndex}`} clipPath="url(#cut-off)">
                <rect
                  key={`shift-${shiftIndex}`}
                  style={{ fill: shiftIndex % 2 === 0 ? "#ECEFF1" : "#CFD8DC" }}
                  fillOpacity={0.5}
                  x={shiftStart}
                  y={0}
                  width={Math.min(shiftWidth, dimensions.boundedWidth - shiftStart)}
                  height={dimensions.boundedHeight} />

                <text y={17}
                  x={xScale(shift.beginDatetime) + 5}
                  fill={theme.palette.text.primary}
                  fontWeight="bold"
                  fontSize="1em"
                  fillOpacity="0.9"
                  textAnchor="start">
                  {`${shift.name} - ${formatDate(shift.factoryDay)}`}
                </text>

                <line className="Axis__line" strokeWidth="1" x1={shiftStart} x2={shiftStart} y1={0} y2={dimensions.boundedHeight + 20} />

                <text y={dimensions.boundedHeight + 15}
                  x={shiftStart + 5}
                  fill={theme.palette.text.primary}
                  fontSize="0.8em"
                  textAnchor="start">
                  {formatTimeWithSeconds(shift.beginDatetime as number)}
                </text>
              </g>
            );
          })}
        </g>

        <g clipPath="url(#cut-off)">
          {productionFiltered.slice(sliceRange[0], sliceRange[1]).map((production: IProduction, productionIndex: number) => {

            if (production.productionMachines.length === 0) {
              return null;
            }

            heatGroup += currentProductHeight;
            currentProductHeight = heatHeight * production.productionMachines.length + orderHeight;

            const productionKey = `production-${production.productionID}`;
            const lastProductionItem = production.productionMachines[production.productionMachines.length - 1];
            const referencePos = {
              x: xProductionEndAccessorScaled(lastProductionItem, xScale) + 5,
              align: "start"
            };

            if (referencePos.x > dimensions.boundedWidth) {
              referencePos.x = xProductionBeginAccessorScaled(production.productionMachines[0], xScale) - 5;
              referencePos.align = "end";
            }

            const p1 = xProductionBeginAccessorScaled(production, xScale);
            const p2 = xProductionEndAccessorScaled(production, xScale);
            const flagP = (p2 - p1) / (lastProductionItem.reference ?? lastProductionItem.title).length > 8.3;

            return (
              <g key={productionKey}>
                <rect
                  fill={productionIndex % 2 === 0 ? "#fff" : "#ddd"}
                  fillOpacity={0.4}
                  x={0}
                  y={heatGroup - 20}
                  width={dimensions.boundedWidth}
                  height={currentProductHeight} />
                <line className="Axis__line" strokeWidth="0.5" x1={0} x2={dimensions.boundedWidth} y1={heatGroup-20} y2={heatGroup - 20} />

                {production.productionMachines.map((productionItem: IProductionExtended, heatIndex: number) => {
                  const heatKey = `${chartID}-${productionKey}-${productionItem.title}-${heatIndex}`;
                  const heatY = heatIndex * heatHeight;

                  const variables: IVariableGroup[] = [];
                  productionItem.variables.forEach(variable => {
                    const item = variables.find(x => x.datetime === variable.datetime);
                    if (!item) {
                      variables.push({
                        datetime: variable.datetime,
                        variables: [ variable ]
                      });
                    } else {
                      item.variables.push(variable);
                    }
                  });

                  const endTimeList = productionItem.delays.map(d => d.endDatetime ?? getCurrentDatetimeInSeconds());
                  const end = xScale(Math.max(...endTimeList, productionItem.endDatetime ?? getCurrentDatetimeInSeconds())) - xProductionBeginAccessorScaled(productionItem, xScale);

                  const heatData = productionItem.__typename === "EAFProduction" ? productionItem as IEAFProduction : null;
                  const rmData = productionItem.__typename === "StrandProduction" ? productionItem as IStrandProduction : null;
                  const productWidth = Math.max(end, xProductionEndAccessorScaled(productionItem, xScale) - xProductionBeginAccessorScaled(productionItem, xScale), 0);
                  const flag = productWidth / productionItem.machine.name.length > 8.3;

                  return(
                    <g key={heatKey} transform={`translate(0, ${heatGroup + 12})`}  height={heatHeight} onClick={() => onSelected(productionItem)}>
                      {productTooltip(
                        productionItem,
                        (heatData && HeatDetailInfo(heatData)) || (rmData && StrandDetailInfo(rmData)),
                        <rect
                          cursor="pointer"
                          fill={getPalette().statusColor.production.background}
                          x={xProductionBeginAccessorScaled(productionItem, xScale)}
                          y={heatY}
                          width={productWidth}
                          height={17}
                        />)}

                      <DelayLayerGantt delays={productionItem.delays} height={17} y={heatY} xAccessorScaled={xAccessorScaled}
                        widthAccessorScaled={widthAccessorScaled} selected={null} chartKey={heatKey} />

                      <SamplesLayerGantt chartID={heatKey} title={productionItem.title} samples={productionItem.samples}
                        y={heatY - 13} xAccessorScaled={sample => xSampleAccessorScaled(sample, xScale) - 4} />

                      <VariablesLayerGantt chartID={heatKey} title={productionItem.title} variableGroups={variables} y={heatY - 13} xAccessorScaled={variableGroup => xScale(variableGroup.datetime) - 5} />

                      {heatData &&
                      <AdditionsLayerGantt chartID={heatKey} title={productionItem.title}
                        additions={heatData.additions}
                        xAccessorScaled={(addition: IAddition) => xAdditionAccessorScaled(addition, xScale)}
                        y={heatY - 12}/>}

                      {productTooltip(
                        productionItem,
                        (heatData && HeatDetailInfo(heatData)) || (rmData && StrandDetailInfo(rmData)),
                        <text
                          y={heatY + 12}
                          x={xProductionBeginAccessorScaled(productionItem, xScale) + productWidth / 2 }
                          fontSize="0.8em"
                          textAnchor="middle"
                          fill={flag ? "white" : "#444"}
                          fontWeight={"bold"}
                          onClick={() => onSelected(productionItem)}>
                          {productionItem.machine.name}
                        </text>)}
                    </g>
                  );
                })}

                <HtmlTooltip title={
                  <div className="display-flex-column align-items-center" >
                    <strong>{lastProductionItem.reference ?? lastProductionItem.title}</strong>
                    <KeyValueItem name="Begin"  value={formatTimeWithSeconds(production.beginDatetime)} />
                    <KeyValueItem name="End"  value={formatTimeWithSeconds(production.endDatetime)} />
                    {production.fields.map((field, index) => {
                      return (
                        <KeyValueItem key={`${lastProductionItem.reference}-${index}`} name={field.name}  value={field.value} unit={field.unit} />
                      );
                    })}
                  </div>
                }>
                  <g>
                    <g transform={`translate(${p1}, ${heatGroup - 17})`}>
                      <polygon fill={"#444"} stroke={"#444"}
                        points={`0 0 0 17 5 15 ${p2 - p1 - 5} 15 ${p2 - p1} 17 ${p2 - p1} 0`}/>
                    </g>
                    <text
                      y={heatGroup - 5}
                      x={xProductionBeginAccessorScaled(production, xScale) + (xProductionEndAccessorScaled(production, xScale) - xProductionBeginAccessorScaled(production, xScale)) / 2 }
                      fontSize="1em"
                      textAnchor="middle"
                      fill={flagP ? "white" : "#aaa"}
                      fontWeight={"bold"}>
                      {lastProductionItem.reference ?? lastProductionItem.title}
                    </text>
                  </g>
                </HtmlTooltip>
              </g>
            );
          })}
        </g>


        <g clipPath="url(#cut-off)">
          {(xCursor >= 0 || xCursor <= dimensions.boundedWidth) &&
            <>
              <line
                x1={xCursor}
                x2={xCursor}
                y1={20}
                y2={dimensions.boundedHeight}
                strokeWidth={2}
                stroke="#333"
                strokeDasharray="4"
              />

              <text
                fill={theme.palette.text.primary}
                className="Axis__tick"
                x={xCursor}
                y={15}
                textAnchor={"middle"}>
                Now
              </text>
            </>
          }
        </g>

      </Chart>
      {openDetail && selectedProductionItem &&
      <Detail closed={() => setOpenDetail(false)} fullWidth={true} maxWidth={"xl"} title="Detail">
        <>
          {selectedProductionItem.__typename === "EAFProduction" && <HeatInformation productionItem={selectedProductionItem} height={500}/>}
          {selectedProductionItem.__typename === "StrandProduction" && <ProductionInformation productionItem={selectedProductionItem as IStrandProduction} height={500}/>}
        </>
      </Detail>
      }
    </div>
  );
};

export default GanttChartParallelMachines;
